import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ThreeDots } from "react-loader-spinner";
import { Button, Tab, Tabs, Tooltip } from "@mui/material";
import { toast } from "react-toastify";
import { confirmAlert } from "react-confirm-alert";
import moment from "moment";
import { OtaUpdatesWrapper } from "./style/otaUpdates";
import UploadRelease from "./UploadRelease";
import NoRecord from "../../assets/SharedComponents/Table/NoRecord";
import Pagination from "../../assets/SharedComponents/Pagination";
import RecordCount from "../../assets/SharedComponents/RecordCount/RecordCount";
import { FaFilterIcon } from "../../assets/Images/icon";
import API from "../../api/axios";
import { APP_URLS } from "../../api/url";
import { OSTYPE, OSTYPE_INVERSE, OTA_FILE_TYPE, OTA_PLATFORM_SLUG, versionRegex } from "../../config/constant";
import { APIError, checkForSuperAdmin } from "../../core/utils";

const formatBytes = (bytes) => {
  if (bytes == null) return "-";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const formatDate = (value) =>
  value ? moment(value).tz("Europe/Amsterdam").format("DD-MM-YYYY HH:mm") : "-";

const shortHash = (sha) => (sha ? `${sha.slice(0, 12)}…` : "-");

// Axios transport failures (network down, timeout) carry no `response`, and
// APIError dereferences it, so fall back to the raw error message there.
const toastError = (error) =>
  toast.error(error?.response ? APIError(error) : error?.message || "Something went wrong!", {
    position: toast.POSITION.TOP_RIGHT,
  });

const OtaUpdates = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState(OSTYPE.IOS);
  const platformSlug = OTA_PLATFORM_SLUG[tab];

  const [rows, _rows] = useState([]);
  const [limits, _limits] = useState(null);
  const [loading, _loading] = useState(true);
  const [currentPage, _currentPage] = useState(1);
  const [countValue, _countValue] = useState("");
  const [filters, _filters] = useState(false);
  const [total, _total] = useState(0);
  const [totalPage, _totalPage] = useState(0);
  const [statusFilter, _statusFilter] = useState("all");
  const [versionFilter, _versionFilter] = useState("");
  const [appliedVersionFilter, _appliedVersionFilter] = useState("");
  const [busy, _busy] = useState(false);
  // Rapid tab/filter/page changes fire overlapping list requests. Only the most
  // recent one may update state, otherwise rows from another platform could be
  // shown (and acted on) under the current platformSlug.
  const listRequestSeq = useRef(0);

  const fetchRows = useCallback(() => {
    const seq = ++listRequestSeq.current;
    _loading(true);
    let url = `${APP_URLS.OTA_FILES}?status=${statusFilter}&platform=${platformSlug}&page=${currentPage}&per_page=${countValue}`;
    if (appliedVersionFilter) url += `&runtime_version=${appliedVersionFilter}`;
    API.get(url)
      .then((res) => {
        if (seq !== listRequestSeq.current) return;
        const resp = res.data;
        if (resp.success === true) {
          _rows(resp.data.data);
          _total(resp.data.total);
          _totalPage(resp.data.last_page);
          _limits(resp.data.limits);
        } else {
          toast.error(resp.message, { position: toast.POSITION.TOP_RIGHT });
        }
        _loading(false);
      })
      .catch((error) => {
        if (seq !== listRequestSeq.current) return;
        _loading(false);
        toastError(error);
      });
  }, [platformSlug, statusFilter, currentPage, countValue, appliedVersionFilter]);

  useEffect(() => {
    if (!checkForSuperAdmin()) {
      navigate("/dashboard", { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
    _currentPage(1);
  };

  const applyVersionFilter = () => {
    const v = versionFilter.trim();
    if (v && !versionRegex.test(v)) {
      toast.error("Version filter must look like 3.2.0.", { position: toast.POSITION.TOP_RIGHT });
      return;
    }
    _appliedVersionFilter(v);
    _currentPage(1);
  };

  const clearFilter = () => {
    _filters(true);
    _countValue("");
    _statusFilter("all");
    _versionFilter("");
    _appliedVersionFilter("");
    _currentPage(1);
  };

  const copyUrl = (url) => {
    navigator.clipboard?.writeText(url).then(
      () => toast.success("URL copied.", { position: toast.POSITION.TOP_RIGHT }),
      () => toast.error("Could not copy URL.", { position: toast.POSITION.TOP_RIGHT })
    );
  };

  const runAction = (action, version, successMessage) => {
    _busy(true);
    API.post(`${APP_URLS.OTA_FILES}/${platformSlug}/${version}/${action}`)
      .then((res) => {
        const resp = res.data;
        _busy(false);
        if (resp.success === true) {
          toast.success(successMessage, { position: toast.POSITION.TOP_RIGHT });
          fetchRows();
        } else {
          toast.error(resp.message, { position: toast.POSITION.TOP_RIGHT });
        }
      })
      .catch((error) => {
        _busy(false);
        toastError(error);
      });
  };

  const handleDeactivate = (version) => {
    if (busy) return;
    confirmAlert({
      title: "Deactivate OTA release",
      message: `Take ${OSTYPE_INVERSE[tab]} ${version} offline? Devices on ${version} will get no OTA update until you roll back or upload again. Devices that already applied it keep it.`,
      closeOnEscape: true,
      buttons: [
        {
          label: "Yes, deactivate",
          onClick: () => runAction("deactivate", version, `${OSTYPE_INVERSE[tab]} ${version} deactivated.`),
        },
        { label: "No" },
      ],
      closeOnClickOutside: true,
      overlayClassName: "overlay-custom-class-name",
    });
  };

  const handleRollback = (version) => {
    if (busy) return;
    _busy(true);
    // Show the admin exactly which archived files will come back before they confirm.
    API.get(`${APP_URLS.OTA_FILES}?status=archived&platform=${platformSlug}&runtime_version=${version}&per_page=10`)
      .then((res) => {
        _busy(false);
        const archived = res.data?.data?.data || [];
        const targets = {};
        archived.forEach((row) => {
          if (row.archive_path && !targets[row.file_type]) targets[row.file_type] = row;
        });
        const manifest = targets[OTA_FILE_TYPE.MANIFEST];
        const jsbundle = targets[OTA_FILE_TYPE.JSBUNDLE];
        if (!manifest && !jsbundle) {
          toast.error(`No previous ${OSTYPE_INVERSE[tab]} ${version} files to roll back to.`, {
            position: toast.POSITION.TOP_RIGHT,
          });
          return;
        }
        const describe = (row) =>
          row ? `${row.original_filename} (${formatBytes(row.file_size_bytes)}, uploaded ${formatDate(row.uploaded_at)})` : "none archived";
        confirmAlert({
          title: "Roll back OTA release",
          message: `Restore these files for ${OSTYPE_INVERSE[tab]} ${version}? The currently live files (if any) will be archived.`,
          childrenElement: () => (
            <p className="mt-2 mb-0">
              <b>Manifest:</b> {describe(manifest)}
              <br />
              <b>JS bundle:</b> {describe(jsbundle)}
            </p>
          ),
          closeOnEscape: true,
          buttons: [
            {
              label: "Yes, roll back",
              onClick: () => runAction("rollback", version, `${OSTYPE_INVERSE[tab]} ${version} rolled back.`),
            },
            { label: "No" },
          ],
          closeOnClickOutside: true,
          overlayClassName: "overlay-custom-class-name",
        });
      })
      .catch((error) => {
        _busy(false);
        toastError(error);
      });
  };

  // Rollback/deactivate act on the whole version (manifest + bundle), so the
  // buttons are drawn once per version group rather than on every file row.
  const seenActionKeys = new Set();
  const actionsFor = (row) => {
    const key = `${row.runtime_version}-${row.is_active ? "live" : "archived"}`;
    if (seenActionKeys.has(key)) return null;
    seenActionKeys.add(key);

    if (row.is_active) {
      return (
        <div className="action-btns">
          <Button variant="contained" disabled={busy} onClick={() => handleRollback(row.runtime_version)}>
            Roll back
          </Button>
          <Button variant="contained" className="bg-danger" disabled={busy} onClick={() => handleDeactivate(row.runtime_version)}>
            Deactivate
          </Button>
        </div>
      );
    }
    if (row.archive_path) {
      return (
        <div className="action-btns">
          <Button variant="contained" className="bg-success" disabled={busy} onClick={() => handleRollback(row.runtime_version)}>
            Restore
          </Button>
        </div>
      );
    }
    return null;
  };

  return (
    <OtaUpdatesWrapper>
      <div className="auctionMainContainer">
        <div className="row mt-4">
          <div className="col-md-4 offset-md-4 mb-2">
            <h1>OTA Updates</h1>
            <Tabs
              value={tab}
              onChange={handleTabChange}
              textColor="primary"
              indicatorColor="primary"
              centered
              sx={{ "& .MuiTabs-indicator": { backgroundColor: "#607afc" } }}
            >
              <Tab
                value={OSTYPE.IOS}
                label="IOS"
                sx={{ color: tab === OSTYPE.IOS ? "#607afc !important" : "gray", fontWeight: "bold" }}
              />
              <Tab
                value={OSTYPE.ANDROID}
                label="Android"
                sx={{ color: tab === OSTYPE.ANDROID ? "#607afc !important" : "gray", fontWeight: "bold" }}
              />
            </Tabs>
          </div>
        </div>

        {limits && !limits.upload_supported && (
          <div className="limits-warning">
            This server's PHP upload limit is {formatBytes(limits.php_max_upload_kb * 1024)}, which is
            smaller than a full release ({formatBytes((limits.max_jsbundle_kb + limits.max_manifest_kb) * 1024)}).
            Uploads are disabled until <code>upload_max_filesize</code> / <code>post_max_size</code> are raised.
          </div>
        )}

        <div className="d-flex align-items-center justify-content-between info-wrap">
          <div className="justify-content-end mb-2 me-2">
            <div className="actionContainer">
              <UploadRelease OSType={tab} limits={limits} onUploaded={fetchRows} />
            </div>
          </div>
          <div className="justify-content-end mb-2 d-flex gap-2 mb-2 select-info align-items-center">
            <select
              className="filter-select"
              value={statusFilter}
              onChange={(e) => {
                _statusFilter(e.target.value);
                _currentPage(1);
              }}
            >
              <option value="all">All</option>
              <option value="active">Live</option>
              <option value="archived">Archived</option>
            </select>
            <div className="history-filter">
              <input
                type="text"
                placeholder="Version e.g. 3.2.0"
                value={versionFilter}
                onChange={(e) => _versionFilter(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && applyVersionFilter()}
              />
              <button className="btn btn-secondary" onClick={applyVersionFilter}>
                Filter
              </button>
            </div>
            <RecordCount
              onSearchClick={(search) => _countValue(search)}
              id="pageCount"
              val={countValue}
              filters={filters}
              _filters={_filters}
            />
            <Tooltip title="Clear Filter" placement="bottom">
              <FaFilterIcon className="filter-icon" role="button" onClick={clearFilter} />
            </Tooltip>
          </div>
        </div>
        <div className="d-flex align-items-center justify-content-between info-wrap">
          <div className="justify-content-end mt-2 me-2">
            <span className="text-start">
              Total Records : <b>{total}</b>
            </span>
          </div>
        </div>
        <div className="tableContainer">
          <table className="auctionTable">
            <thead className="tableHead">
              <tr className="table-row">
                <th className="time-start">Runtime Version</th>
                <th className="time-start">File</th>
                <th className="time-start">Name / Size</th>
                <th className="time-start">SHA-256</th>
                <th className="time-start">Uploaded</th>
                <th className="time-start">Status</th>
                <th className="time-start">Public URL</th>
                <th className="time-start actions-col">Actions</th>
              </tr>
            </thead>
            {loading ? (
              <tbody>
                <tr>
                  <td colSpan={8}>
                    <div className="d-flex justify-content-center three-dot">
                      <ThreeDots height="150" width="150" radius="9" color="#637df4" visible={true} />
                    </div>
                  </td>
                </tr>
              </tbody>
            ) : (
              <tbody>
                {rows.length > 0 ? (
                  rows.map((row) => (
                    <tr key={`ota-${row.id}`} className={`table-row file-row${row.is_active ? " live-row" : ""}`}>
                      <td>
                        <b>{row.runtime_version}</b>
                      </td>
                      <td>{row.file_type === OTA_FILE_TYPE.MANIFEST ? "Manifest" : "JS bundle"}</td>
                      <td>
                        {row.original_filename}
                        <div className="muted">{formatBytes(row.file_size_bytes)}</div>
                      </td>
                      <td>
                        <Tooltip title={row.sha256 || ""} placement="top">
                          <span className="mono">{shortHash(row.sha256)}</span>
                        </Tooltip>
                      </td>
                      <td>
                        {formatDate(row.uploaded_at)}
                        <div className="muted">by user #{row.uploaded_by ?? "-"}</div>
                      </td>
                      <td>
                        {row.is_active ? (
                          <span className="status-live">Live</span>
                        ) : (
                          <>
                            <span className="status-archived">Archived</span>
                            <div className="muted">{formatDate(row.deactivated_at)}</div>
                          </>
                        )}
                      </td>
                      <td>
                        {row.latest_public_url ? (
                          <span className="copy-link" onClick={() => copyUrl(row.latest_public_url)}>
                            Copy URL
                          </span>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td className="actions-col">{actionsFor(row)}</td>
                    </tr>
                  ))
                ) : (
                  <NoRecord />
                )}
              </tbody>
            )}
          </table>
        </div>
        <div>
          {totalPage > 1 ? (
            <Pagination
              totalPages={totalPage}
              page={currentPage}
              key={totalPage}
              onPageClick={(page) => _currentPage(page + 1)}
            />
          ) : (
            ""
          )}
        </div>
      </div>
    </OtaUpdatesWrapper>
  );
};

export default OtaUpdates;
